import type { FilterOptionsState } from '@mui/material/useAutocomplete';

/** What may stand between two parts of a number: "123_2", "123 2", "123/2". */
const SEPARATORS = /[\s_/\\.,;:–-]+/g;

/**
 * A text as the link search compares it: lower case, every run of separators
 * one underscore. "123/2", "123 - 2" and "123_2" all read "123_2".
 */
export function normalizeLinkSearch(text: string): string {
    return text
        .toLowerCase()
        .replace(SEPARATORS, '_')
        .replace(/^_+|_+$/g, '');
}

/** Whether any of the texts contains the query, separators read loosely. */
export function matchesLinkSearch(
    query: string,
    texts: readonly string[]
): boolean {
    const needle = normalizeLinkSearch(query);
    if (!needle) {
        return true;
    }
    return texts.some((text) => normalizeLinkSearch(text).includes(needle));
}

/**
 * The `filterOptions` of every field that links a record: an option matches
 * when one of its texts (number, label, …) contains what was typed, however
 * the parts of a number are separated.
 */
export function linkSearchFilter<Option>(
    textsOf: (option: Option) => readonly string[]
) {
    return (options: Option[], state: FilterOptionsState<Option>): Option[] =>
        options.filter((option) =>
            matchesLinkSearch(state.inputValue, textsOf(option))
        );
}
